/**
 * SourcesTabContent - the matter's Documents tab.
 *
 * TWO SECTIONS, NOT TEN TABS.
 *
 *   Case files        the paper of this matter - contracts, evidence,
 *                     correspondence, whatever was uploaded about the dispute.
 *   Legal authority   everything the argument stands on - legislation, case
 *                     law, preparatory works, regulations, agency decisions,
 *                     connected data sources - whether a person uploaded it or
 *                     the AI retrieved it.
 *
 * What used to be a tab is now a filter CHIP inside Legal authority, because
 * the corpus a source happens to live in is a detail and "is this ours or the
 * machine's?" is not. Origin is on every row, in both sections, and is its own
 * filter: it was previously a 10px caption in a grid view nobody used, and the
 * table's Origin column was never switched on.
 *
 * The derivation - sections, corpora, origin, search, counts - is pure and
 * lives in `sources/sourceModel`, with tests. This file is the container: it
 * owns the fetches, the upload flows, the AI-evaluation state and the reader.
 */

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { translate } from '../../../i18n/translate';
import { useAppDispatch } from '../../../store';
import { hideModal, showModal } from '../../../store/actions/modalActions';
import { showSidePanel, hideSidePanel } from '../../../store/actions/sidePanelActions';
import { useHistory, useLocation } from 'react-router-dom';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import { getMultiHubSocketService } from '../../../utils/MultiHubSocketService';
import Button from '../../shared/TailwindComponents/Button';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import {
  PiFileLight,
  PiScalesLight,
  PiGavelLight,
  PiTrashLight,
  PiFolderOpenLight,
  PiDatabaseLight,
  PiMagnifyingGlassLight,
} from 'react-icons/pi';
import { MatterData } from '../MatterDetails';
import { hasCasesSupport } from '../matterConstants';
import {
  fetchMatterSources,
  addFileToMatter,
  uploadLegalSource,
  streamMatterSource,
  removeSourceFromMatter,
  downloadDocument,
  deleteDocument,
} from '../../../store/actions/Matters/MatterActions';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import { useGenerateMore } from '../../../hooks/useGenerateMore';
import FindMoreProgress from '../FindMoreProgress';
import SourceGeneratePopover from './SourceGeneratePopover';
import apis from '../../../utils/apis';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, sourcesItemsTourSteps } from '../../shared/FeatureTour';
import {
  AUTHORITY_CORPORA,
  MatterSource,
  SourceCorpus,
  SourceOrigin,
  SourceSection,
  countByCorpus,
  countByOrigin,
  countBySection,
  filterSources,
  isCorpusAvailable,
  normaliseSources,
  sortSources,
} from './sources/sourceModel';
import SourceRow from './sources/SourceRow';
import CaseFilesTable from './sources/CaseFilesTable';
import AddSourceMenu from './sources/AddSourceMenu';
import { CorpusChips, OriginFilter, SectionSegments } from './sources/SourceFilters';
import Spinner from '../../shared/TailwindComponents/Spinner';

interface SourcesTabContentProps {
  matterData: MatterData | null;
  sources: any | null;
  setSources: React.Dispatch<React.SetStateAction<any>>;
  loading: boolean;
  userAccessLevel: 'edit' | 'admin' | 'view';
  onUpdate: () => void;
  hasDocuments?: boolean;
}

/** Corpus → the `useGenerateMore` step that fills it. Data sources have none. */
const CORPUS_GENERATE_STEP: Partial<Record<SourceCorpus, string>> = {
  legislation: 'laws',
  case_law: 'cases',
  preparatory_works: 'preparatory_works',
  regulations: 'regulations',
  agency_decisions: 'agency_decisions',
};

/**
 * Corpus → the `sourceType` the reader expects.
 *
 * Unchanged from the tabbed version on purpose: `ReaderComponent` renders its
 * metadata sidebar off this, and this rebuild is the list, not the reader.
 */
const CORPUS_READER_TYPE: Record<SourceCorpus, 'file' | 'law' | 'case' | 'document'> = {
  case_file: 'document',
  legislation: 'law',
  case_law: 'case',
  preparatory_works: 'law',
  regulations: 'law',
  agency_decisions: 'law',
  data_sources: 'case',
};

const SourcesTabContent: React.FC<SourcesTabContentProps> = ({
  matterData,
  sources,
  setSources,
  loading,
  userAccessLevel,
  onUpdate,
  hasDocuments = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const { checkSectionGate } = useLicenseGate();
  const canEdit = userAccessLevel === 'edit' || userAccessLevel === 'admin';
  const matterUuid = matterData?.uuid || matterData?.MatterID || '';
  const jurisdiction = matterData?.jurisdiction || matterData?.Jurisdiction || '';
  const showCases = hasCasesSupport(jurisdiction);

  /* ── View state ──────────────────────────────────────────────── */
  const [section, setSection] = useState<SourceSection>('case_files');
  /*
    Deep link from the context pane's "Authorities" tile: `?section=…` names
    which of the two lists to open. Consumed once and stripped, so a later
    click on the same tile is a fresh change the effect can see.
  */
  useEffect(() => {
    const wanted = new URLSearchParams(location.search).get('section');
    if (wanted !== 'case_files' && wanted !== 'legal_authority') return;
    setSection(wanted);
    const params = new URLSearchParams(location.search);
    params.delete('section');
    history.replace({ pathname: location.pathname, search: params.toString() ? `?${params}` : '' });
  }, [location.search, location.pathname, history]);
  const [corpus, setCorpus] = useState<SourceCorpus | null>(null);
  const [origin, setOrigin] = useState<SourceOrigin | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [deletingItems, setDeletingItems] = useState<string[]>([]);
  const [isTabLoading, setIsTabLoading] = useState(loading);
  const [evaluatingSourceIds, setEvaluatingSourceIds] = useState<Set<string>>(new Set());
  const evalGroupsRef = useRef<string[]>([]);
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  /* ── "Find more" per corpus. All five mount always, so a generation
        started before a reload is picked back up from sessionStorage by the
        hook regardless of which chip is showing. ─────────────────── */
  const laws = useGenerateMore({ matterUuid, targetStep: 'laws', onComplete: onUpdate });
  const cases = useGenerateMore({ matterUuid, targetStep: 'cases', onComplete: onUpdate });
  const prepWorks = useGenerateMore({ matterUuid, targetStep: 'preparatory_works', onComplete: onUpdate });
  const regulations = useGenerateMore({ matterUuid, targetStep: 'regulations', onComplete: onUpdate });
  const agency = useGenerateMore({ matterUuid, targetStep: 'agency_decisions', onComplete: onUpdate });

  const generators = useMemo(() => ({
    legislation: laws,
    case_law: cases,
    preparatory_works: prepWorks,
    regulations,
    agency_decisions: agency,
  } as Partial<Record<SourceCorpus, ReturnType<typeof useGenerateMore>>>), [
    laws, cases, prepWorks, regulations, agency,
  ]);

  const [availableIndexCategories, setAvailableIndexCategories] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!jurisdiction) return;
    let cancelled = false;
    apis.get(`/matters/available-indexes?country=${encodeURIComponent(jurisdiction)}`)
      .then((res: any) => {
        if (cancelled) return;
        const indexes: { category: string }[] = res.data?.indexes ?? [];
        setAvailableIndexCategories(new Set(indexes.map((idx) => idx.category)));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [jurisdiction]);

  // Cleanup PubSub listeners on unmount
  useEffect(() => {
    const groups = evalGroupsRef;
    return () => {
      const multiHubService = getMultiHubSocketService();
      groups.current.forEach((group) => {
        multiHubService.emit('ai_processing', 'leave', { group });
      });
    };
  }, []);

  useEffect(() => {
    setIsTabLoading(loading);
  }, [loading]);

  /* ── The list ────────────────────────────────────────────────── */

  const items = useMemo(
    () => normaliseSources(sources, { showCases }),
    [sources, showCases]
  );

  const sectionCounts = useMemo(() => countBySection(items), [items]);

  const corpusCounts = useMemo(
    () => countByCorpus(items, { origin, search: searchTerm }),
    [items, origin, searchTerm]
  );

  const corpusAvailability = useMemo(() => {
    const out = {} as Record<SourceCorpus, boolean>;
    AUTHORITY_CORPORA.forEach((id) => {
      // Case law is not offered at all where the jurisdiction has no such
      // corpus, so it must not claim to be available.
      const jurisdictionAllows = id === 'case_law' ? showCases : true;
      out[id] = jurisdictionAllows && isCorpusAvailable(id, availableIndexCategories, corpusCounts[id]);
    });
    out.case_file = true;
    return out;
  }, [availableIndexCategories, corpusCounts, showCases]);

  const originCounts = useMemo(
    () => countByOrigin(items, { section, corpus, search: searchTerm }),
    [items, section, corpus, searchTerm]
  );

  const visibleItems = useMemo(
    () => sortSources(filterSources(items, { section, corpus, origin, search: searchTerm })),
    [items, section, corpus, origin, searchTerm]
  );

  /** Everything in the section, ignoring chips and search - for empty states. */
  const sectionTotal = section === 'case_files'
    ? sectionCounts.case_files
    : sectionCounts.legal_authority;

  const {
    isRunning: isItemsTourRunning,
    handleJoyrideCallback: handleItemsTourCallback,
  } = useFeatureTour({
    tourId: 'sources-items',
    steps: sourcesItemsTourSteps,
    readyToShow: items.length > 0 && !loading,
    autoStartDelay: 600,
  });

  /* ── Data refresh ────────────────────────────────────────────── */

  /** Refetch and hand back the NORMALISED list, so callers can diff sections. */
  const refreshSourcesData = useCallback(async (): Promise<MatterSource[] | null> => {
    if (!matterUuid) return null;
    setIsTabLoading(true);
    try {
      const freshSources = await dispatch(fetchMatterSources(matterUuid));
      setSources(freshSources);
      onUpdate();
      return normaliseSources(freshSources, { showCases });
    } catch {
      return null;
    } finally {
      setIsTabLoading(false);
    }
  }, [matterUuid, dispatch, setSources, onUpdate, showCases]);

  /**
   * SAY WHERE IT LANDED.
   *
   * A file uploaded with a legal `document_type` is re-routed by the backend
   * into laws.json, so it never shows up under Case files. It used to just be
   * gone: the upload panel closed, the list did not change, and nothing said
   * why. Now the sections are counted either side of the upload and the toast
   * names the one that grew.
   */
  const announceLanding = useCallback(
    (before: ReturnType<typeof countBySection>, after: MatterSource[] | null, fallback: string) => {
      if (!after) return;
      const now = countBySection(after);
      const grewAuthority = now.legal_authority > before.legal_authority;
      const grewCaseFiles = now.case_files > before.case_files;

      if (grewAuthority && !grewCaseFiles) {
        setSection('legal_authority');
        setCorpus(null);
        dispatch(showFeedbackModal({
          showModal: true,
          modalType: 'success',
          message: t('Added to Legal authority'),
          subMsg: t('It was recognised as a legal source, so it sits with the authority rather than the case files.'),
          duration: 5000,
        }));
        return;
      }

      if (grewCaseFiles) {
        setSection('case_files');
        dispatch(showFeedbackModal({
          showModal: true,
          modalType: 'success',
          message: t('Added to Case files'),
          duration: 3000,
        }));
        return;
      }

      dispatch(showFeedbackModal({
        showModal: true, modalType: 'success', message: fallback, duration: 3000,
      }));
    },
    [dispatch, t]
  );

  /* ── Upload flows (unchanged endpoints, unchanged panels) ────── */

  const handleUploadCaseFiles = () => {
    if (!checkSectionGate('files')) return;
    dispatch(showSidePanel({
      type: 'uploadAttachment',
      showSidePanel: true,
      size: 'md',
      message: t('Upload files'),
      icon: <PiFileLight className="w-5 h-5" />,
      subMsg: t('Evidence, contracts, briefs, pleadings, memos, or other case-related documents.'),
      onConfirm: async (data: any) => {
        const uploadFiles: File[] = data.files || data;
        if (!uploadFiles.length || !matterUuid) return;

        const before = countBySection(items);
        setIsTabLoading(true);

        try {
          await dispatch(addFileToMatter(matterUuid, uploadFiles));
          const after = await refreshSourcesData();
          announceLanding(before, after, t('Upload complete'));
        } finally {
          setIsTabLoading(false);
        }
      },
      files: [],
      onCancel: () => dispatch(hideSidePanel()),
    } as any));
  };

  /**
   * Upload a legal source and track its AI relevance evaluation.
   *
   * KEPT WHOLESALE: PubSub for the live state, a 15s poll as the fallback when
   * a message is missed, and a 3-minute stop so a row can never be stuck
   * showing a spinner forever.
   */
  const processSourceUpload = async (
    uuid: string,
    sourceType: 'law' | 'case',
    sourceData: any,
    file?: File
  ) => {
    if (!file) return null;
    const result = await dispatch(uploadLegalSource(uuid, file, sourceType, sourceData?.title));
    const evalCorrelationId = result?.evaluation_correlation_id;
    const evalFileUuids: string[] = result?.evaluated_file_uuids || [];
    if (evalCorrelationId && evalFileUuids.length > 0) {
      setEvaluatingSourceIds((prev) => {
        const next = new Set(prev);
        evalFileUuids.forEach((id: string) => next.add(id));
        return next;
      });

      let evalComplete = false;
      const clearEvalState = () => {
        evalComplete = true;
        setEvaluatingSourceIds((prev) => {
          const next = new Set(prev);
          evalFileUuids.forEach((id: string) => next.delete(id));
          return next;
        });
        refreshSourcesData();
      };

      const groupName = `source_eval_${evalCorrelationId}`;
      evalGroupsRef.current.push(groupName);
      const multiHubService = getMultiHubSocketService();
      await multiHubService.connectToHub('ai_processing');
      multiHubService.emit('ai_processing', 'join', { group: groupName });
      const handleEvalMsg = (message: any) => {
        const d = message?.data || message;
        const msgGroupId = d?.groupId || d?.group_id;
        if (msgGroupId && msgGroupId !== groupName) return;
        if (d?.status === 'completed' || d?.status === 'error') {
          multiHubService.emit('ai_processing', 'leave', { group: groupName });
          multiHubService.off('ai_processing', '*', handleEvalMsg);
          clearEvalState();
        }
      };
      multiHubService.on('ai_processing', '*', handleEvalMsg);

      // Fallback polling: if PubSub doesn't deliver completion within 45s,
      // poll the sources API to check if evaluation finished
      const pollInterval = setInterval(async () => {
        if (evalComplete) {
          clearInterval(pollInterval);
          return;
        }
        try {
          const freshSources = await dispatch(fetchMatterSources(uuid));
          const sourceFile = sourceType === 'law' ? 'laws' : 'precedents';
          const docs = freshSources?.[sourceFile]?.documents || freshSources?.[sourceFile] || [];
          const docsArray = Array.isArray(docs) ? docs : [];
          const allDone = evalFileUuids.every((fid: string) => {
            const doc = docsArray.find((d: any) => d.id === fid || d.file_uuid === fid);
            return doc && doc.evaluation_status === 'completed';
          });
          if (allDone) {
            multiHubService.emit('ai_processing', 'leave', { group: groupName });
            multiHubService.off('ai_processing', '*', handleEvalMsg);
            clearEvalState();
            clearInterval(pollInterval);
          }
        } catch {
          // Silent - keep polling
        }
      }, 15000);

      // Safety: stop polling after 3 minutes regardless
      setTimeout(() => {
        clearInterval(pollInterval);
        if (!evalComplete) clearEvalState();
      }, 180000);
    }
    return result;
  };

  const handleAddLegalSource = (sourceType: 'law' | 'case') => {
    if (!checkSectionGate('files')) return;
    if (!matterUuid) return;

    const isLaw = sourceType === 'law';
    dispatch(showSidePanel({
      type: 'addSource',
      showSidePanel: true,
      size: 'md',
      sourceType,
      message: isLaw ? t('Add legislation') : t('Add precedent'),
      icon: isLaw ? <PiScalesLight className="w-5 h-5" /> : <PiGavelLight className="w-5 h-5" />,
      subMsg: isLaw
        ? t('Upload one or more law, statute, or regulation documents. They will be automatically evaluated by AI for relevance.')
        : t('Upload one or more court case or precedent documents. They will be automatically evaluated by AI for relevance.'),
      onConfirm: async (data: any) => {
        const before = countBySection(items);
        setIsTabLoading(true);
        try {
          if (data.files && data.files.length > 1) {
            for (const file of data.files) {
              await processSourceUpload(matterUuid, sourceType, { title: file.name.replace(/\.[^/.]+$/, '') }, file);
            }
          } else {
            await processSourceUpload(matterUuid, sourceType, data.sourceData, data.file);
          }
          const after = await refreshSourcesData();
          announceLanding(before, after, isLaw ? t('Legislation added') : t('Precedent added'));
        } finally {
          setIsTabLoading(false);
        }
      },
      onCancel: () => dispatch(hideSidePanel()),
    } as any));
  };

  /* ── Reader ──────────────────────────────────────────────────── */

  const handleOpenItem = async (item: MatterSource) => {
    if (!matterUuid) return;

    const readerType = CORPUS_READER_TYPE[item.corpus];
    const blobPath = item.blobPath || item.blob_path;

    // Derive actual content type from blob_path extension when missing or generic
    const deriveContentType = (path: string, originalType?: string): string => {
      if (originalType && originalType !== 'application/octet-stream') return originalType;
      switch (path.split('.').pop()?.toLowerCase()) {
        case 'html': case 'htm': return 'text/html';
        case 'pdf': return 'application/pdf';
        case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
        case 'json': return 'application/json';
        case 'xml': return 'application/xml';
        case 'md': case 'markdown': return 'text/markdown';
        case 'txt': return 'text/plain';
        default: return originalType || 'application/octet-stream';
      }
    };
    const resolvedContentType = blobPath ? deriveContentType(blobPath, item.contentType) : item.contentType;

    if (!blobPath) {
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('File path not available for this source'),
        showModal: true,
        duration: 3000,
      }));
      return;
    }

    /** Shared metadata for every state the reader passes through. */
    const readerCitation = (extra: Record<string, any> = {}) => ({
      ...item,
      title: item.name,
      name: item.name,
      snippet: item.content,
      summary: item.summary,
      description: item.description,
      keywords: item.keywords,
      practicalApplication: item.practicalApplication,
      country: item.country,
      language: item.language,
      relevanceScore: item.relevanceScore,
      justification: item.justification,
      relevantSections: item.relevantSections,
      encrypted: item.encrypted,
      encryptionFormat: item.encryptionFormat,
      contentType: resolvedContentType || (item.url?.endsWith('.pdf') ? 'application/pdf' : 'text/html'),
      ...extra,
    });

    /*
      ALWAYS THE GLOBAL SIDE PANEL, at 85% - the same mechanism the review
      document viewer uses.

      This briefly opened in the surface's docked Reader pane instead. A pane
      caps at 900px and shares the row with the canvas, which is the right
      trade for glancing at a citation and the wrong one for actually reading
      a contract: reading is a full-attention act and wants the width. The
      Reader pane still exists for anything else that wants the docked
      behaviour; matter Documents does not.
    */
    const showReader = (citation: any, isLoading: boolean) => {
      dispatch(showSidePanel({
        type: 'reader',
        matterId: matterUuid,
        sourceType: readerType as any,
        userAccessLevel,
        citation,
        showSidePanel: true,
        loading: isLoading,
        message: item.name,
        icon: <PiGavelLight className="w-6 h-6 text-gray-600" />,
        size: 'xl',
        subMsg: t(item.corpus === 'case_file' ? 'Case file' : 'Legal authority'),
        onDelete: refreshSourcesData,
      }));
    };

    // Metadata first, with a loader over it while the blob streams.
    showReader(readerCitation(), true);

    try {
      const streamResult = await dispatch(streamMatterSource(
        blobPath,
        'inline',
        resolvedContentType,
        // A datasource file lives in `service-providers`, not the matter's own
        // container; without this the endpoint looks in the wrong one.
        String(item.container || (item as any).Container || '').trim() || undefined,
      )) as any;

      if (!streamResult.success || !streamResult.blob) {
        // Stop loading - metadata plus the "no content" fallback.
        showReader(
          readerCitation({ blob_path: undefined, blobPath: undefined, contentType: resolvedContentType }),
          false
        );
        return;
      }

      showReader(
        readerCitation({
          blob: streamResult.blob,
          blobObject: streamResult.blob,
          encrypted: false,
          encryptionFormat: undefined,
          contentType: streamResult.contentType || resolvedContentType || 'application/octet-stream',
        }),
        false
      );
    } catch {
      showReader(readerCitation({ contentType: resolvedContentType }), false);
    }
  };

  /* ── Row actions ─────────────────────────────────────────────── */

  const handleDownloadDocument = async (item: MatterSource) => {
    const blobPath = item.blobPath || item.blob_path;
    if (!matterUuid || !blobPath) {
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: translate('File path not available for this document'),
        showModal: true,
        duration: 3000,
      }));
      return;
    }
    try {
      await dispatch(downloadDocument(matterUuid, blobPath, item.name || 'document') as any);
    } catch {
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: translate('Error downloading document. Please try again.'),
        showModal: true,
        duration: 3000,
      }));
    }
  };

  const removeOne = async (item: MatterSource) => {
    if (item._sourceType === 'document') {
      // Documents are addressed by blob path, which is what `id` holds here.
      await dispatch(deleteDocument(matterUuid, item.id) as any);
    } else {
      await dispatch(removeSourceFromMatter(matterUuid, item._sourceType as any, item.id) as any);
    }
  };

  const handleDeleteItem = (item: MatterSource) => {
    if (!matterUuid) return;
    dispatch(showModal({
      showModal: true,
      type: 'confirmation',
      title: t('Remove source'),
      message: t('Remove this source from the matter?'),
      subText: t('It will be permanently removed. This action cannot be undone.'),
      icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
      variant: 'danger',
      confirmLabel: t('Remove'),
      onConfirm: async () => {
        dispatch(hideModal());
        setDeletingItems((prev) => [...prev, item.id]);
        try {
          await removeOne(item);
          await refreshSourcesData();
        } finally {
          setDeletingItems((prev) => prev.filter((d) => d !== item.id));
        }
      },
      onClose: () => dispatch(hideModal()),
    } as any));
  };

  const handleBulkRemove = () => {
    if (selectedSourceIds.length === 0) return;
    dispatch(showModal({
      showModal: true,
      type: 'confirmation',
      title: t('Remove {{count}} sources', { count: selectedSourceIds.length }),
      message: t('Remove the selected sources from the matter?'),
      subText: t('They will be permanently removed. This action cannot be undone.'),
      icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
      variant: 'danger',
      confirmLabel: t('Remove'),
      onConfirm: async () => {
        dispatch(hideModal());
        setIsBulkDeleting(true);
        setDeletingItems((prev) => [...prev, ...selectedSourceIds]);
        try {
          for (const id of selectedSourceIds) {
            const item = items.find((i) => i.id === id);
            if (item) await removeOne(item);
          }
          await refreshSourcesData();
        } finally {
          setDeletingItems((prev) => prev.filter((d) => !selectedSourceIds.includes(d)));
          setSelectedSourceIds([]);
          setIsBulkDeleting(false);
        }
      },
      onClose: () => dispatch(hideModal()),
    } as any));
  };

  const toggleSelect = (id: string) =>
    setSelectedSourceIds((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  /* ── Generation, scoped to the chip in view ──────────────────── */

  const activeGenerator = corpus ? generators[corpus] : undefined;
  const generateStep = corpus ? CORPUS_GENERATE_STEP[corpus] : undefined;

  /**
   * Which generation, if any, takes over the list.
   *
   * With a chip selected it is that corpus's. On "All" it is whichever is
   * running - so a "find more laws" started before a reload is still visible
   * when the hook resumes it from sessionStorage, rather than running unseen.
   */
  const takeoverGenerator = useMemo(() => {
    if (section !== 'legal_authority') return null;
    const candidates = corpus
      ? [generators[corpus]]
      : AUTHORITY_CORPORA.map((id) => generators[id]);
    return candidates.find(
      (g) => g && (g.isGenerating || g.progress.status === 'complete' || g.progress.status === 'failed')
    ) || null;
  }, [section, corpus, generators]);

  /* ── Render ──────────────────────────────────────────────────── */

  if (isTabLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div className="animate-fadePulse bg-surface-sunken h-8 w-48 rounded-full" />
          <div className="animate-fadePulse bg-surface-sunken h-8 w-24 rounded" />
        </div>
        <Skeleton variant="list" rows={6} />
      </div>
    );
  }

  if (!matterData) {
    return (
      <div className="p-6 text-center text-fg-muted">{t('Loading sources...')}</div>
    );
  }

  const addMenu = (
    <AddSourceMenu
      canEdit={canEdit}
      showCases={showCases}
      onUploadFiles={handleUploadCaseFiles}
      onAddLegislation={() => handleAddLegalSource('law')}
      onAddPrecedent={() => handleAddLegalSource('case')}
    />
  );

  /** The empty state, which has to distinguish three different nothings. */
  const renderEmpty = () => {
    const unavailable = corpus ? !corpusAvailability[corpus] : false;

    // 1. This jurisdiction has no such corpus - not a missing document.
    if (unavailable) {
      return (
        <div className="flex flex-col items-center justify-center text-center py-14 px-6">
          <div className="h-12 w-12 rounded-full bg-surface-sunken flex items-center justify-center mb-3">
            <PiDatabaseLight className="h-6 w-6 text-fg-muted" />
          </div>
          <h3 className="text-fg-body font-medium mb-1">{t('Not available in this jurisdiction')}</h3>
          <p className="text-fg-muted text-sm max-w-md">
            {t('PONS has no {{corpus}} source for {{jurisdiction}} yet. Sources you upload yourself still appear here.', {
              corpus: t(corpus ? corpus.replace(/_/g, ' ') : ''),
              jurisdiction: jurisdiction || t('this jurisdiction'),
            })}
          </p>
        </div>
      );
    }

    // 2. Something is here, the filters just hide it.
    if (sectionTotal > 0) {
      return (
        <div className="flex flex-col items-center justify-center text-center py-14 px-6">
          <PiMagnifyingGlassLight className="h-6 w-6 text-fg-muted mb-2" />
          <p className="text-fg-muted text-sm">{t('No documents match these filters.')}</p>
          <Button
            variant="outline"
            size="small"
            className="mt-3"
            onClick={() => { setCorpus(null); setOrigin(null); setSearchTerm(''); }}
          >
            {t('Clear filters')}
          </Button>
        </div>
      );
    }

    // 3. Genuinely nothing yet.
    const isFiles = section === 'case_files';
    return (
      <div className="flex flex-col items-center justify-center text-center py-14 px-6">
        <div className="h-12 w-12 rounded-full bg-surface-sunken flex items-center justify-center mb-3">
          {isFiles
            ? <PiFolderOpenLight className="h-6 w-6 text-champagne" />
            : <PiScalesLight className="h-6 w-6 text-champagne" />}
        </div>
        <h3 className="text-fg-body font-medium mb-1">
          {isFiles ? t('No case files yet') : t('No legal authority yet')}
        </h3>
        <p className="text-fg-muted text-sm max-w-md">
          {isFiles
            ? t('Upload evidence, contracts, briefs, pleadings, and other documents about this matter.')
            : t('Add legislation and precedents yourself, or let the AI find them from your case files.')}
        </p>
        <div className="flex items-center gap-2 mt-4">
          {addMenu}
          {!isFiles && corpus && generateStep && (
            <SourceGeneratePopover
              jurisdiction={jurisdiction}
              sourceTab={generateStep}
              isGenerating={!!activeGenerator?.isGenerating}
              onGenerate={(selectedIndexes) => activeGenerator?.handleGenerateMore(selectedIndexes)}
              hasDocuments={hasDocuments}
              currentListCount={0}
              canEdit={canEdit}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {/* ── Section segments + the one Add control ─────────────── */}
      <div data-tour-id="sources-sub-tabs" className="flex items-center justify-between gap-3 flex-wrap">
        <SectionSegments
          section={section}
          counts={sectionCounts}
          onChange={(next) => {
            setSection(next);
            setCorpus(null);
            setSelectedSourceIds([]);
          }}
        />
        <div className="flex items-center gap-2">
          {section === 'legal_authority' && corpus && generateStep && (
            <SourceGeneratePopover
              jurisdiction={jurisdiction}
              sourceTab={generateStep}
              isGenerating={!!activeGenerator?.isGenerating}
              onGenerate={(selectedIndexes) => activeGenerator?.handleGenerateMore(selectedIndexes)}
              hasDocuments={hasDocuments}
              currentListCount={visibleItems.length}
              canEdit={canEdit}
            />
          )}
          {addMenu}
        </div>
      </div>

      <p className="text-xs text-fg-muted leading-relaxed">
        {section === 'case_files'
          ? t('Evidence, contracts, briefs, pleadings, correspondence, and other documents about this matter.')
          : t('Everything this matter argues from — uploaded or found by AI. Filter by type or origin.')}
      </p>

      {/* ── Filters ─────────────────────────────────────────────── */}
      {section === 'legal_authority' && (
        <CorpusChips
          corpus={corpus}
          counts={corpusCounts}
          total={sectionCounts.legal_authority}
          available={corpusAvailability}
          onChange={(next) => { setCorpus(next); setSelectedSourceIds([]); }}
        />
      )}

      {/* ── One header row: filters left, then count, then search ────────
            The count badge and the search used to sit at opposite ends of an
            otherwise empty bar. They are one cluster - "how many of these am
            I looking at, and let me narrow it" - so they travel together on
            the right, count first. */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* Origin is filterable in BOTH sections; in Case files everything is
            an upload, so the control only earns its place under authority. */}
        <div className="flex items-center gap-2 min-w-0">
          {section === 'legal_authority' && (
            <OriginFilter origin={origin} counts={originCounts} onChange={setOrigin} />
          )}
        </div>
        <div className="flex-1 min-w-[220px]">
          <FilterBarWithTabs
            onSearchChange={setSearchTerm}
            controlsOnRight
            noParentPadding
            noParentHeight
            noBorder
            itemCount={visibleItems.length}
            totalItemCount={sectionTotal}
            itemCountLabel={sectionTotal === 1 ? t('document') : t('documents')}
            selectionCount={selectedSourceIds.length}
            actions={canEdit && selectedSourceIds.length > 0 ? [{
              label: t('Remove Selected'),
              onClick: handleBulkRemove,
              icon: <PiTrashLight className="h-4 w-4 text-danger-500" />,
            }] : []}
          />
        </div>
      </div>

      {/* ── The list ────────────────────────────────────────────── */}
      <div data-tour-id="sources-table-area" className="relative min-h-[320px]">
        {isBulkDeleting && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-bg/80 backdrop-blur-[2px] rounded-lg">
            <Spinner size="glyph" className="!h-10 !w-10" />
            <p className="mt-3 text-sm font-medium text-fg-strong">
              {t('Removing {{count}} sources...', { count: selectedSourceIds.length || deletingItems.length })}
            </p>
          </div>
        )}

        {takeoverGenerator ? (
          <FindMoreProgress
            progress={takeoverGenerator.progress}
            isGenerating={takeoverGenerator.isGenerating}
          />
        ) : visibleItems.length === 0 ? (
          renderEmpty()
        ) : section === 'case_files' ? (
          /* Case files are FILES - who uploaded them, when and how big is the
             whole question, and that is a table. Legal authority stays a
             ranked row list, where relevance and origin are the question. */
          <CaseFilesTable
            items={visibleItems}
            selectedIds={selectedSourceIds}
            selectable={canEdit}
            deletingIds={deletingItems}
            evaluatingIds={evaluatingSourceIds}
            onOpen={handleOpenItem}
            onSelect={toggleSelect}
            onSelectAll={() => {
              const allIds = visibleItems.map((item) => item.id);
              setSelectedSourceIds((prev) =>
                allIds.every((id) => prev.includes(id)) ? [] : allIds
              );
            }}
            onDownload={handleDownloadDocument}
            onDelete={canEdit ? handleDeleteItem : undefined}
          />
        ) : (
          <div className="space-y-0">
            {visibleItems.map((item, index) => {
              const evaluating =
                evaluatingSourceIds.has(item.id) ||
                evaluatingSourceIds.has(String(item.file_uuid || '')) ||
                item.evaluationStatus === 'pending';
              return (
                <SourceRow
                  key={item.id || `${item.corpus}-${index}`}
                  item={item}
                  index={index}
                  isEvaluating={evaluating}
                  isSelected={selectedSourceIds.includes(item.id)}
                  selectable={canEdit}
                  isDeleting={deletingItems.includes(item.id)}
                  onOpen={() => handleOpenItem(item)}
                  onSelect={() => toggleSelect(item.id)}
                  onDelete={canEdit ? () => handleDeleteItem(item) : undefined}
                  onDownload={
                    item.corpus === 'case_file' ? () => handleDownloadDocument(item) : undefined
                  }
                />
              );
            })}
          </div>
        )}
      </div>

      <FeatureTour
        steps={sourcesItemsTourSteps}
        run={isItemsTourRunning}
        onCallback={handleItemsTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default SourcesTabContent;
